import { z } from 'src/lib'
import { OperacionAFOCLFields, OperacionAFOCLTablaValorFields } from './fields'
import { schemaEntityWithId } from 'src/types'

export const schemaFormOperacionAFOCLTablaValor = z.object({
    [OperacionAFOCLTablaValorFields.CodInstrumento]: z.number(),
    [OperacionAFOCLTablaValorFields.DescInstrumento]: z.string(),
    [OperacionAFOCLTablaValorFields.CodOperacionTipo]: z.number(),
    [OperacionAFOCLTablaValorFields.DescOperacionTipo]: z.string(),
    [OperacionAFOCLTablaValorFields.ValorPuente]: z
        .string({
            invalid_type_error: 'El aforo es requerido',
            required_error: 'El aforo es requerido'
        })
        .nullable()
        .refine(val => val !== null && val !== '', {
            message: 'El aforo es requerido'
        }),
    [OperacionAFOCLTablaValorFields.ValorCliente]: z
        .string({
            invalid_type_error: 'El aforo es requerido',
            required_error: 'El aforo es requerido'
        })
        .nullable()
        .refine(val => val !== null && val !== '', {
            message: 'El aforo es requerido'
        }),
    [OperacionAFOCLTablaValorFields.OrdenInstrumento]: z.number(),
});

export type FormOperacionAFOCLTablaValor = z.infer<typeof schemaFormOperacionAFOCLTablaValor>;

const baseSchemaFormOperacionAFOCL = z.object({
    [OperacionAFOCLFields.CodPais]: z.number({ required_error: 'El país es requerido.' }),
    [OperacionAFOCLFields.CodUnidadNegocio]: z.number({ required_error: 'La unidad de negocio es requerida.' }),
    [OperacionAFOCLFields.CodSectorComercial]: z.coerce.number({ invalid_type_error: 'El sector comercial es requerido.' }).min(1, { message: 'El sector comercial es requerido.' }),
    [OperacionAFOCLFields.FechaSolicitud]: z.date({ required_error: 'La fecha de solicitud es requerida.' }),
    [OperacionAFOCLFields.FechaVencimiento]: z.date({ required_error: 'La fecha de vencimiento es requerida.' }),
    [OperacionAFOCLFields.Observaciones]: z.string().optional().nullable(),
    [OperacionAFOCLFields.NumeroCuenta]: z.string(),
    [OperacionAFOCLFields.RazonSocial]: z.string(),
    [OperacionAFOCLFields.TablaValores]: z.array(schemaFormOperacionAFOCLTablaValor)
        .min(1, { message: 'Debe completar los aforos.' }),
});

export const schemaFormOperacionAFOCL = baseSchemaFormOperacionAFOCL
    .refine(
        (schema: z.infer<typeof baseSchemaFormOperacionAFOCL>) => {
            if (schema.fechaVencimiento && schema.fechaSolicitud) {
                return schema.fechaVencimiento.getTime() >= schema.fechaSolicitud.getTime();
            }

            return true;
        },
        {
            message: 'La fecha de vencimiento debe ser mayor o igual a la fecha de solicitud.',
            path: [OperacionAFOCLFields.FechaVencimiento],
        }
    );

export type FormOperacionAFOCL = z.infer<typeof schemaFormOperacionAFOCL>

export const schemaOperacionAFOCL = z
    .object({
        [OperacionAFOCLFields.CodOperacionAFOCL]: z.number(),
        [OperacionAFOCLFields.CodPais]: z.number(),
        [OperacionAFOCLFields.NombrePais]: z.string(),
        [OperacionAFOCLFields.CodUnidadNegocio]: z.number().nullable(),
        [OperacionAFOCLFields.DescUnidadNegocio]: z.string().nullable(),
        [OperacionAFOCLFields.CodSectorComercial]: z.number(),
        [OperacionAFOCLFields.DescSectorComercial]: z.string(),
        [OperacionAFOCLFields.FechaSolicitud]: z.coerce.date(),
        [OperacionAFOCLFields.FechaVencimiento]: z.coerce.date(),
        [OperacionAFOCLFields.NumeroCuenta]: z.string(),
        [OperacionAFOCLFields.RazonSocial]: z.string(),
        [OperacionAFOCLFields.CodEstado]: z.number(),
        [OperacionAFOCLFields.DescEstado]: z.string(),
        [OperacionAFOCLFields.FechaAprobacion]: z.coerce.date().nullable(),
        [OperacionAFOCLFields.FechaAlta]: z.coerce.date().nullable(),
        [OperacionAFOCLFields.IdUsuarioAlta]: z.number(),
        [OperacionAFOCLFields.NombreUsuarioAlta]: z.string(),
        [OperacionAFOCLFields.ApellidoUsuarioAlta]: z.string(),
        [OperacionAFOCLFields.Observaciones]: z.string().nullable(),
        [OperacionAFOCLFields.EstadoFinalizado]: z.boolean(),
        [OperacionAFOCLFields.PermiteEditarOperacion]: z.boolean(),
        [OperacionAFOCLFields.PermiteEliminarOperacion]: z.boolean(),
    })
    .merge(schemaEntityWithId)

export type OperacionAFOCL = z.infer<typeof schemaOperacionAFOCL>